import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

// Components
import Header from '../components/landing/Header';
import CheckoutForm from '../components/landing/CheckoutForm';
import OrderSummary from '../components/landing/OrderSummary';
import Footer from '../components/landing/Footer';
import { Button } from 'primereact/button';

const CheckoutPage = () => {
    const { theme, toggleTheme } = useTheme();
    const { items, clearCart } = useCart();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState({
        shipping: {},
        payment: {},
        notes: ''
    });

    // Redirigir si el carrito está vacío
    React.useEffect(() => {
        if (items.length === 0) {
            navigate('/carrito');
        }
    }, [items, navigate]);

    // Checkout en un solo proceso sin pasos

    const handleOrderComplete = async (orderResult) => {
        try {
            // Limpiar carrito después de completar la orden
            clearCart();

            // Redirigir a página de confirmación
            navigate(`/orden-confirmada/${orderResult.id}`, {
                state: { order: orderResult }
            });
        } catch (error) {
            console.error('Error completando orden:', error);
        }
    };

    if (items.length === 0) {
        return null; // El useEffect redirigirá
    }

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Checkout - Laptops.pe</title>
                <meta name="description" content="Completa tu compra en Laptops.pe — La casa de las laptops en Perú." />
                <meta property="og:title" content="Checkout - Laptops.pe" />
                <meta property="og:description" content="Completa tu compra en Laptops.pe — La casa de las laptops en Perú." />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL}/checkout`} />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />

            <main className="landing-main w-full">
                <div className="w-full py-4 px-3">
                    <div className="mb-4">
                        <h1 className="text-4xl font-bold mb-2">Checkout</h1>
                        <p className="text-color-secondary">
                            Completa tu información para finalizar la compra
                        </p>
                    </div>

                    {/* Checkout sin pasos: dos columnas en dispositivos grandes */}

                    <div className="grid">
                        {/* Izquierda: Formulario de Checkout */}
                        <div className="col-12 lg:col-8">
                            <div className="surface-card border-round p-4 h-full">
                                {!isAuthenticated && (
                                    <div className="mb-4 p-3 border-round" style={{ backgroundColor: 'var(--yellow-50)', border: '1px solid var(--yellow-200)' }}>
                                        <div className="flex align-items-center">
                                            <i className="pi pi-info-circle text-yellow-600 mr-2"></i>
                                            <span className="text-yellow-800">
                                                ¿Ya tienes una cuenta?
                                                <Button
                                                    label="Iniciar Sesión"
                                                    className="p-button-link p-0 ml-2"
                                                    onClick={() => navigate('/login', { state: { returnTo: '/checkout' } })}
                                                />
                                                para una experiencia más rápida.
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <CheckoutForm
                                    orderData={orderData}
                                    onOrderDataChange={setOrderData}
                                    onOrderComplete={handleOrderComplete}
                                    user={user}
                                />
                            </div>
                        </div>

                        {/* Derecha: Resumen del Pedido */}
                        <div className="col-12 lg:col-4">
                            <div className="surface-card border-round p-4 h-full">
                                <h2 className="text-2xl font-semibold mb-3">Resumen del Pedido</h2>
                                <OrderSummary
                                    orderData={orderData}
                                    extraFee={orderData?.extraFee || 0}
                                    extraFeeLabel={orderData?.extraFeeLabel || 'Recargo por método de pago'}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default CheckoutPage;
